import React, { useState, useEffect, useRef, useMemo } from 'react';
import {CKEditor, useCKEditorCloud} from '@ckeditor/ckeditor5-react';

import './CdkAppEditor.css';
import HTMLViewer from "../HTMLViewer";
import {Col, Row, Spinner} from "react-bootstrap";
import DropdownItemsInput from "../DropdownItemsInput";
import {FaCheckSquare} from "react-icons/fa";
import {useDynamicFields} from "../../hooks/query/useDynamicFields";
import {ACCESS_TOKEN, CLIENT_ID} from "../../constants";

const LICENSE_KEY = process.env.REACT_APP_CDK_KEY;

const DEFAULT_HEX_COLORS = [
    { color: '#000000', label: 'Black' },
    { color: '#4D4D4D', label: 'Dim grey' },
    { color: '#999999', label: 'Grey' },
    { color: '#E6E6E6', label: 'Light grey' },
    { color: '#FFFFFF', label: 'White', hasBorder: true },
    { color: '#E65C5C', label: 'Red' },
    { color: '#E69C5C', label: 'Orange' },
    { color: '#E6E65C', label: 'Yellow' },
    { color: '#C2E65C', label: 'Light green' },
    { color: '#5CE65C', label: 'Green' },
    { color: '#5CE6A6', label: 'Aquamarine' },
    { color: '#5CE6E6', label: 'Turquoise' },
    { color: '#5CA6E6', label: 'Light blue' },
    { color: '#5C5CE6', label: 'Blue' },
    { color: '#A65CE6', label: 'Purple' }
];

function CdkAppEditor(props) {
    const editorContainerRef = useRef(null);
    const editorRefMain = useRef(null);
    const editorRef = useRef(null);
    const cloud = useCKEditorCloud({ version: '47.1.0', premium: true });
    const [isLayoutReady, setIsLayoutReady] = useState(false);

    const {data: dynamicFieldList, isLoading, error} = useDynamicFields();
    const [dynamicValue, setDynamicValue] = useState([]);
    const [standardDynamicValue, setStandardDynamicValue] = useState([]);
    const [customDynamicValue, setCustomDynamicValue] = useState([]);
    const [newCustomDynamicArray, setNewCustomDynamicArray] = useState([]);
    const [customDynamicValueError, setCustomDynamicValueError] = useState('');
    const customDynamicInputRef = useRef();
    const api = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5132/api';


    useEffect(() => {
        if (dynamicFieldList?.dynamic?.length) {
            dynamicFieldList.dynamic.map((item, index) => {
                setDynamicValue((prevData) => [
                    ...prevData,
                    {
                        id: index,
                        data: `{{${item}}}`
                    }
                ])
            });
        }

        if (dynamicFieldList?.standard?.length) {
            dynamicFieldList.standard.map((item, index) => {
                setStandardDynamicValue((prevData) => [
                    ...prevData,
                    {
                        id: index,
                        data: `{{${item}}}`
                    }
                ])
            });
        }

        if (dynamicFieldList?.custom?.length) {
            dynamicFieldList.custom.map((item, index) => {
                setCustomDynamicValue((prevData) => [
                    ...prevData,
                    {
                        id: index,
                        data: `{{${item}}}`
                    }
                ])
            });
        }
    }, [dynamicFieldList]);

    useEffect(() => {
        setIsLayoutReady(true);

        return () => setIsLayoutReady(false);
    }, []);

    const { ClassicEditor, editorConfig } = useMemo(() => {
        if (cloud.status !== 'success' || !isLayoutReady) {
            return {};
        }

        const {
            ClassicEditor,
            Autosave,
            Essentials,
            Paragraph,
            AutoImage,
            Autoformat,
            ImageBlock,
            BlockQuote,
            Bold,
            Emoji,
            FontBackgroundColor,
            FontColor,
            FontFamily,
            FontSize,
            GeneralHtmlSupport,
            Heading,
            Highlight,
            ImageCaption,
            ImageInsertViaUrl,
            ImageResize,
            ImageStyle,
            ImageTextAlternative,
            ImageToolbar,
            ImageUpload,
            ImageInline,
            Indent,
            IndentBlock,
            Italic,
            Link,
            LinkImage,
            List,
            ListProperties,
            Mention,
            Table,
            TableCaption,
            TableCellProperties,
            TableColumnResize,
            TableLayout,
            TableProperties,
            TableToolbar,
            TextTransformation,
            TodoList,
            Underline,
            Alignment,
            ImageInsert,
            SimpleUploadAdapter,
            PasteFromOffice,
            CloudServices
        } = cloud.CKEditor;
        const { getEmailInlineStylesTransformations, ExportWord, ExportInlineStyles } = cloud.CKEditorPremiumFeatures;

        return {
            ClassicEditor,
            editorConfig: {
                toolbar: {
                    items: [
                        'undo',
                        'redo',
                        '|',
                        'exportWord',
                        '|',
                        'heading',
                        '|',
                        'fontSize',
                        'fontFamily',
                        'fontColor',
                        'fontBackgroundColor',
                        '|',
                        'bold',
                        'italic',
                        'underline',
                        '|',
                        'emoji',
                        'link',
                        'insertImage',
                        'insertTable',
                        'highlight',
                        'blockQuote',
                        '|',
                        'alignment',
                        '|',
                        'bulletedList',
                        'numberedList',
                        'todoList',
                        'outdent',
                        'indent'
                    ],
                    shouldNotGroupWhenFull: false
                },
                plugins: [
                    Alignment,
                    Autoformat,
                    AutoImage,
                    Autosave,
                    SimpleUploadAdapter,
                    BlockQuote,
                    Bold,
                    CloudServices,
                    Emoji,
                    Essentials,
                    ExportInlineStyles,
                    ExportWord,
                    FontBackgroundColor,
                    FontColor,
                    FontFamily,
                    FontSize,
                    GeneralHtmlSupport,
                    Heading,
                    Highlight,
                    ImageBlock,
                    ImageCaption,
                    ImageInline,
                    ImageInsert,
                    ImageInsertViaUrl,
                    ImageResize,
                    ImageStyle,
                    ImageTextAlternative,
                    ImageToolbar,
                    ImageUpload,
                    Indent,
                    IndentBlock,
                    Italic,
                    Link,
                    LinkImage,
                    List,
                    ListProperties,
                    Mention,
                    Paragraph,
                    PasteFromOffice,
                    Table,
                    TableCaption,
                    TableCellProperties,
                    TableColumnResize,
                    TableLayout,
                    TableProperties,
                    TableToolbar,
                    TextTransformation,
                    TodoList,
                    Underline
                ],
                exportInlineStyles: {
                    stylesheets: [
                        'https://cdn.ckeditor.com/ckeditor5/47.1.0/ckeditor5.css',
                        'https://cdn.ckeditor.com/ckeditor5-premium-features/47.1.0/ckeditor5-premium-features.css'
                    ],
                    stripCssClasses: true,
                    transformations: getEmailInlineStylesTransformations()
                },
                exportWord: {
                    stylesheets: [
                        'https://cdn.ckeditor.com/ckeditor5/47.1.0/ckeditor5.css',
                        'https://cdn.ckeditor.com/ckeditor5-premium-features/47.1.0/ckeditor5-premium-features.css'
                    ],
                    fileName: 'export-word.docx',
                    converterOptions: {
                        document: {
                            orientation: 'portrait',
                            size: 'Tabloid',
                            margins: {
                                top: '20mm',
                                bottom: '20mm',
                                right: '24mm',
                                left: '24mm'
                            }
                        }
                    }
                },
                fontBackgroundColor: {
                    colorPicker: {
                        format: 'hex'
                    },
                    colors: DEFAULT_HEX_COLORS
                },
                fontColor: {
                    colorPicker: {
                        format: 'hex'
                    },
                    colors: DEFAULT_HEX_COLORS
                },
                fontFamily: {
                    supportAllValues: true
                },
                fontSize: {
                    options: [10, 12, 14, 'default', 18, 20, 22],
                    supportAllValues: true
                },
                heading: {
                    options: [
                        {
                            model: 'paragraph',
                            title: 'Paragraph',
                            class: 'ck-heading_paragraph'
                        },
                        {
                            model: 'heading1',
                            view: 'h1',
                            title: 'Heading 1',
                            class: 'ck-heading_heading1'
                        },
                        {
                            model: 'heading2',
                            view: 'h2',
                            title: 'Heading 2',
                            class: 'ck-heading_heading2'
                        },
                        {
                            model: 'heading3',
                            view: 'h3',
                            title: 'Heading 3',
                            class: 'ck-heading_heading3'
                        },
                        {
                            model: 'heading4',
                            view: 'h4',
                            title: 'Heading 4',
                            class: 'ck-heading_heading4'
                        },
                        {
                            model: 'heading5',
                            view: 'h5',
                            title: 'Heading 5',
                            class: 'ck-heading_heading5'
                        },
                        {
                            model: 'heading6',
                            view: 'h6',
                            title: 'Heading 6',
                            class: 'ck-heading_heading6'
                        }
                    ]
                },
                htmlSupport: {
                    allow: [
                        {
                            name: /^(div|table|tbody|tr|td|span|img|h1|h2|h3|p|a)$/,
                            styles: true,
                            attributes: true,
                            classes: true
                        }
                    ]
                },
                image: {
                    toolbar: [
                        'toggleImageCaption',
                        'imageTextAlternative',
                        '|',
                        'imageStyle:inline',
                        'imageStyle:wrapText',
                        'imageStyle:breakText',
                        '|',
                        'resizeImage'
                    ]
                },
                initialData: '',
                licenseKey: LICENSE_KEY,
                link: {
                    addTargetToExternalLinks: true,
                    defaultProtocol: 'https://',
                    decorators: {
                        toggleDownloadable: {
                            mode: 'manual',
                            label: 'Downloadable',
                            attributes: {
                                download: 'file'
                            }
                        }
                    }
                },
                list: {
                    properties: {
                        styles: true,
                        startIndex: true,
                        reversed: false
                    }
                },
                placeholder: 'Enter document text',
                table: {
                    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties'],
                    tableProperties: {
                        borderColors: DEFAULT_HEX_COLORS,
                        backgroundColors: DEFAULT_HEX_COLORS
                    },
                    tableCellProperties: {
                        borderColors: DEFAULT_HEX_COLORS,
                        backgroundColors: DEFAULT_HEX_COLORS
                    }
                },
                simpleUpload: {
                    uploadUrl: `${api}/LetterTemplate/UploadLetterImage`,

                    withCredentials: true,

                    headers: {
                        'ClientId': localStorage.getItem(CLIENT_ID),
                        'Authorization': `Bearer ${JSON.parse(localStorage.getItem(ACCESS_TOKEN))}`,
                    }
                }
            }
        };
    }, [cloud, isLayoutReady]);

    const handleEditorChange = (event, editor) => {
        if (editor) {
            try {
                editor.execute('exportInlineStyles')
                    .then(html => {
                        let emailHtml = html;

                        emailHtml = emailHtml.replace(/<figure/g, '<div').replace(/<\/figure>/g, '</div>');
                        props.result([emailHtml, editor.getData()])
                    })
                    .catch(err => {
                        console.error('Export error:', err);
                    });
            } catch (err) {
                console.error('Export error:', err);
            }
        }
    };

    const handleEditorReady = (editor) => {
        editorRef.current = editor;

        if (props.documentText) {
            editorRef.current.setData(props.documentText);
        }
    };

    useEffect(() => {
        if (editorRef.current && props.documentText) {
            editorRef.current.setData(props.documentText);
        }
    }, [props.documentText]);

    const handleDropdownChange = (value) => {
        insertTextAtCursor(value.data);
    };

    const addCustomDynamicValueToEditor = () => {
        if (!customDynamicInputRef.current.value) {
            setCustomDynamicValueError('Empty dynamic value');

            return;
        }

        const dynamicValuesArray = [...dynamicFieldList.dynamic, ...dynamicFieldList.standard, ...dynamicFieldList.custom]
            .map((item) => item.toLowerCase());
        const newCustomDynamicValue = `{{${customDynamicInputRef.current.value.trim()}}}`;
        const isDuplicatedDynamicValue = dynamicValuesArray.includes(customDynamicInputRef.current.value?.trim()
            ?.toLowerCase());

        if (isDuplicatedDynamicValue) {
            setCustomDynamicValueError('Duplicate dynamic value');

            return;
        }


        insertTextAtCursor(newCustomDynamicValue);
        setCustomDynamicValueError('');
        customDynamicInputRef.current.value = '';
        setNewCustomDynamicArray((prevData) => [...prevData, newCustomDynamicValue]);
        props.setNewCustomDynamicFields([...newCustomDynamicArray, newCustomDynamicValue]);
    }

    const insertTextAtCursor = (text) => {
        const editor = editorRef.current;
        if (!editor) return;

        const view = editor.editing.view;
        const model = editor.model;

        model.change(writer => {
            const insertPosition = model.document.selection.getFirstPosition();
            writer.insertText(text, insertPosition);
        });

        view.focus();
    };

    if (isLoading) return (<div className="text-center py-4">
        <Spinner
            animation="border"
            size="sm"
            variant="primary"
            role="status"
        >
        </Spinner>
    </div>)

    if (error) return (<div> Error to get dynamicFields</div>)

    return (
        <>
            {
                props.dynamicValue && <Row className="background-1">

                    <Col lg={{span: 6, offset: 0}} md={{span: 6, offset: 0}}
                         className="padding-left-unset">
                        <div className="document-form">
                            <label htmlFor="document_title" id="certificateNameLabel"
                                   className="form-label form-spacing">
                                Dynamic Values<span className="asterics"></span></label>
                            <div>
                                <DropdownItemsInput data={dynamicValue}
                                                    result={handleDropdownChange}
                                                    className="background-1 background-1 dropdown-input-content"
                                                    title="Select Dynamic Field"/>
                            </div>
                        </div>
                    </Col>
                    <Col lg={{span: 6, offset: 0}} md={{span: 6, offset: 0}}
                         className="padding-right-unset">
                        <div className="document-form">
                            <label htmlFor="document_title" id="certificateNameLabel"
                                   className="form-label form-spacing">
                                Custom Dynamic Values<span className="asterics"></span></label>
                            <div>
                                <DropdownItemsInput data={customDynamicValue}
                                                    result={handleDropdownChange}
                                                    className="background-1 background-1 dropdown-input-content"
                                                    title="Select Custom Dynamic Field"/>
                            </div>
                        </div>
                    </Col>
                    <Col lg={{span: 6, offset: 0}} md={{span: 6, offset: 0}}
                         className="padding-left-unset">
                        <div className="document-form">
                            <label htmlFor="document_title" id="certificateNameLabel"
                                   className="form-label form-spacing">
                                Standard Dynamic Values<span className="asterics"></span></label>
                            <div>
                                <DropdownItemsInput data={standardDynamicValue}
                                                    result={handleDropdownChange}
                                                    className="background-1 background-1 dropdown-input-content"
                                                    title="Select Standard Dynamic Field"/>
                            </div>
                        </div>
                    </Col>
                    <Col lg={{span: 6, offset: 0}} md={{span: 6, offset: 0}}
                         className="padding-right-unset d-flex">
                        <div className="document-form w-100">
                            <label htmlFor="custom_field" id="documentTitleLabel"
                                   className="form-label form-spacing">
                                New Custom Dynamic Value</label>
                            <input ref={customDynamicInputRef}
                                   type="text"
                                   autoComplete="off"
                                   className="input-valid-form border-gray input-spacing"
                                   placeholder="Add new custom dynamic field here"
                                   id="custom_field"
                            />
                            {customDynamicValueError &&
                                <div className="error-message mt-2 ms-3">
                                    {customDynamicValueError}
                                </div>
                            }
                        </div>

                        <div class="document-form-checkbox">
                            <FaCheckSquare
                                className="checkbox-icon-square"
                                size={40}
                                onClick={addCustomDynamicValueToEditor}
                            />
                        </div>
                    </Col>
                </Row>
            }
            <div className="main-container-cdk mt-4">
                <div className="editor-container-cdk editor-container_classic-editor-cdk" ref={editorContainerRef}>
                    <div className="editor-container__editor-cdk">
                        <div ref={editorRefMain}>
                            {editorConfig &&
                                <CKEditor
                                    editor={ClassicEditor}
                                    config={editorConfig}
                                    onReady={handleEditorReady}
                                    onChange={handleEditorChange}
                                    disabled={props.realOnly}
                                />
                            }
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default CdkAppEditor;